// Endpoint functions for the admins module.
//
// TODO: replace the in-memory mock with real adminApi calls once the
// /api/admin/admins endpoints land. The mock is wrapped to look like a
// real fetch — Promise-returning, AppError-throwing — so swapping it
// later won't ripple into queries, mutations, or containers.

import type { AdminAuditDto, AdminDto, AdminsListDto } from './adminContracts'
import type { GetAdminsListParams, UpsertAdminPayload } from '../types/adminTypes'

// ── Mock latency ───────────────────────────────────────────────────────

const SIMULATED_LATENCY_MS = 200

const delay = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), SIMULATED_LATENCY_MS))

// ── Mock store ─────────────────────────────────────────────────────────

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS
const now = () => new Date()
const ago = (ms: number) => new Date(Date.now() - ms).toISOString()

let nextMockId = 7

const mockStore: AdminDto[] = [
  {
    id: 1,
    name: 'Alexandra Chen',
    email: 'alex.chen@platform.com',
    role: 'Super Admin',
    status: 'Active',
    invitationStatus: 'accepted',
    accessScope: 'Global',
    mfaEnabled: true,
    lastLoginAt: ago(2 * 60 * 1000),
    createdBy: 'System',
    createdAt: ago(120 * DAY_MS),
    lastPermissionChangeAt: ago(2 * HOUR_MS),
  },
  {
    id: 2,
    name: 'Marcus Johnson',
    email: 'marcus.j@platform.com',
    role: 'User Manager',
    status: 'Active',
    invitationStatus: 'accepted',
    accessScope: 'Investors + Orders',
    mfaEnabled: true,
    lastLoginAt: ago(35 * 60 * 1000),
    createdBy: 'Alexandra Chen',
    createdAt: ago(90 * DAY_MS),
    lastPermissionChangeAt: ago(12 * DAY_MS),
  },
  {
    id: 3,
    name: 'Sarah Williams',
    email: 'sarah.w@platform.com',
    role: 'Content Manager',
    status: 'Inactive',
    invitationStatus: 'accepted',
    accessScope: 'Properties only',
    mfaEnabled: false,
    lastLoginAt: ago(96 * DAY_MS),
    createdBy: 'Alexandra Chen',
    createdAt: ago(180 * DAY_MS),
    lastPermissionChangeAt: ago(40 * DAY_MS),
  },
  {
    id: 4,
    name: 'David Kim',
    email: 'david.kim@platform.com',
    role: 'Reports Viewer',
    status: 'Active',
    invitationStatus: 'accepted',
    accessScope: 'Reports read-only',
    mfaEnabled: true,
    lastLoginAt: ago(4 * HOUR_MS),
    createdBy: 'Marcus Johnson',
    createdAt: ago(45 * DAY_MS),
    lastPermissionChangeAt: ago(3 * DAY_MS),
  },
  {
    id: 5,
    name: 'Emily Rodriguez',
    email: 'emily.r@platform.com',
    role: 'Support Manager',
    status: 'Active',
    invitationStatus: 'accepted',
    accessScope: 'Support read-only',
    mfaEnabled: false,
    lastLoginAt: ago(20 * 60 * 1000),
    createdBy: 'Alexandra Chen',
    createdAt: ago(60 * DAY_MS),
    lastPermissionChangeAt: ago(7 * DAY_MS),
  },
  {
    id: 6,
    name: 'Jordan Ali',
    email: 'jordan.ali@platform.com',
    role: 'User Manager',
    status: 'Inactive',
    invitationStatus: 'invited',
    accessScope: 'Investors + Orders',
    mfaEnabled: false,
    lastLoginAt: null,
    createdBy: 'Alexandra Chen',
    createdAt: ago(36 * HOUR_MS),
    lastPermissionChangeAt: null,
  },
]

// ── List + audit ──────────────────────────────────────────────────────

export const getAdminsList = async (params: GetAdminsListParams): Promise<AdminsListDto> => {
  const page = params.page ?? 1
  const limit = params.limit ?? 25
  const offset = (page - 1) * limit
  return delay({
    rows: mockStore.slice(offset, offset + limit),
    total: mockStore.length,
    page,
    limit,
  })
}

export const getAdminAudit = async (id: string): Promise<AdminAuditDto> => {
  const admin = mockStore.find((a) => String(a.id) === id)
  if (!admin) return delay({ events: [] })
  // Synthesised audit: in real life this comes from the audit-log service.
  return delay({
    events: [
      {
        id: `${admin.id}-evt-1`,
        at: ago(2 * HOUR_MS),
        actor: 'Alexandra Chen',
        event: 'Permission updated',
        detail: `Access scope set to ${admin.accessScope}`,
      },
      {
        id: `${admin.id}-evt-2`,
        at: admin.lastLoginAt ?? ago(12 * HOUR_MS),
        actor: admin.name,
        event: 'Signed in',
        detail: 'Web · Chrome · UAE',
      },
      {
        id: `${admin.id}-evt-3`,
        at: admin.createdAt ?? ago(90 * DAY_MS),
        actor: admin.createdBy ?? 'System',
        event: 'Account created',
        detail: `Role: ${admin.role}`,
      },
    ],
  })
}

// ── Mutations ──────────────────────────────────────────────────────────

export const createAdmin = async (payload: UpsertAdminPayload): Promise<AdminDto> => {
  const created: AdminDto = {
    id: nextMockId++,
    name: payload.name,
    email: payload.email,
    role: payload.role,
    status: payload.status,
    accessScope: payload.accessScope ?? 'Reports read-only',
    invitationStatus: 'invited',
    mfaEnabled: false,
    lastLoginAt: null,
    createdBy: 'Alexandra Chen',
    createdAt: now().toISOString(),
    lastPermissionChangeAt: now().toISOString(),
  }
  mockStore.unshift(created)
  return delay(created)
}

export const updateAdmin = async (id: string, payload: UpsertAdminPayload): Promise<AdminDto> => {
  const idx = mockStore.findIndex((admin) => String(admin.id) === id)
  if (idx === -1) throw new Error(`Admin ${id} not found`)
  const updated: AdminDto = {
    ...mockStore[idx],
    ...payload,
    accessScope: payload.accessScope ?? mockStore[idx].accessScope,
    lastPermissionChangeAt: now().toISOString(),
  }
  mockStore[idx] = updated
  return delay(updated)
}

export const deleteAdmin = async (id: string): Promise<void> => {
  const idx = mockStore.findIndex((admin) => String(admin.id) === id)
  if (idx !== -1) mockStore.splice(idx, 1)
  await delay(undefined)
}

export const resendInvite = async (id: string): Promise<void> => {
  const admin = mockStore.find((a) => String(a.id) === id)
  if (admin) admin.invitationStatus = 'invited'
  await delay(undefined)
}

export const suspendAdmin = async (id: string): Promise<void> => {
  const admin = mockStore.find((a) => String(a.id) === id)
  if (admin) admin.status = 'Suspended'
  await delay(undefined)
}

export const resetMfa = async (id: string): Promise<void> => {
  const admin = mockStore.find((a) => String(a.id) === id)
  if (admin) admin.mfaEnabled = false
  await delay(undefined)
}
